import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import {
  Users, MessageSquare, Calendar, CheckCircle, AlertCircle, Info,
  Loader, BarChart3, TrendingUp, Star, Clock, Building2, Heart,
  Globe, Briefcase, Shield, UserCheck, ChevronRight, Plus, Filter,
  Crown, Sparkles
} from 'lucide-react';

// Stakeholder Type Icons
const stakeholderIcons = {
  employees: Users,
  customers: Heart,
  suppliers: Briefcase,
  investors: TrendingUp,
  communities: Globe,
  regulators: Shield,
  ngos: Building2,
  experts: Star
};

// Engagement Card Component
const EngagementCard = ({ engagement }) => {
  const IconComponent = stakeholderIcons[engagement.stakeholder_type] || Users;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`engagement-${engagement.input_id}`}>
      <div className="flex items-start gap-4">
        <div className="p-3 bg-emerald-100 rounded-xl">
          <IconComponent size={24} className="text-emerald-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-bold text-slate-900 capitalize">
              {engagement.stakeholder_type?.replace('_', ' ')}
            </h4>
            <span className="text-xs text-slate-500">{engagement.engagement_method}</span>
          </div>
          <p className="text-sm text-slate-600 mb-2">{engagement.topic_name}</p>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={14}
                  className={star <= engagement.importance_rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}
                />
              ))}
            </div>
            <span className="text-xs text-slate-500">Importance: {engagement.importance_rating}/5</span>
          </div>
          {engagement.concerns?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {engagement.concerns.slice(0, 3).map((concern, idx) => (
                <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                  {concern}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// New Engagement Form Component
const NewEngagementForm = ({ topics, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    stakeholder_type: 'customers',
    topic_id: '',
    importance_rating: 3,
    engagement_method: 'survey',
    concerns: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      concerns: formData.concerns.split(',').map(c => c.trim()).filter(c => c)
    });
    // Reset form
    setFormData({
      stakeholder_type: 'customers',
      topic_id: '',
      importance_rating: 3,
      engagement_method: 'survey',
      concerns: ''
    });
  };

  const stakeholderTypes = [
    'employees', 'customers', 'suppliers', 'investors', 
    'communities', 'regulators', 'ngos', 'experts'
  ];

  const engagementMethods = [
    'survey', 'interview', 'workshop', 'focus_group', 
    'town_hall', 'advisory_panel', 'written_submission'
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Stakeholder Type</label>
          <select 
            value={formData.stakeholder_type}
            onChange={(e) => handleChange('stakeholder_type', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            {stakeholderTypes.map(type => (
              <option key={type} value={type}>{type.replace('_', ' ').toUpperCase()}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Engagement Method</label>
          <select 
            value={formData.engagement_method}
            onChange={(e) => handleChange('engagement_method', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            {engagementMethods.map(method => (
              <option key={method} value={method}>{method.replace('_', ' ').toUpperCase()}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">ESG Topic Discussed</label>
        <select 
          value={formData.topic_id}
          onChange={(e) => handleChange('topic_id', e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          required
        >
          <option value="">Select a topic...</option>
          {Object.entries(topics).map(([id, topic]) => (
            <option key={id} value={id}>{topic.name} ({topic.standard})</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Importance Rating: <span className="font-bold text-emerald-600">{formData.importance_rating}</span>
        </label>
        <input 
          type="range" 
          min="1" 
          max="5"
          value={formData.importance_rating}
          onChange={(e) => handleChange('importance_rating', parseInt(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-slate-500">
          <span>Low</span>
          <span>Medium</span>
          <span>High</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Key Concerns (comma separated)</label>
        <input 
          type="text"
          value={formData.concerns}
          onChange={(e) => handleChange('concerns', e.target.value)}
          placeholder="e.g., carbon emissions, water usage, supply chain"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      <button 
        type="submit"
        disabled={loading || !formData.topic_id}
        className="w-full py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="submit-engagement-btn"
      >
        {loading ? <Loader className="animate-spin" size={18} /> : <Plus size={18} />}
        {loading ? 'Recording...' : 'Record Engagement'}
      </button>
    </form>
  );
};

// Stakeholder Summary Component
const StakeholderSummary = ({ summary }) => {
  if (!summary || !summary.by_stakeholder_type) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Users size={48} className="mx-auto mb-4 opacity-50" />
        <p>No stakeholder engagement data yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
          <p className="text-3xl font-bold text-emerald-600">{summary.total_inputs}</p>
          <p className="text-sm text-emerald-700">Total Engagements</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 text-center">
          <p className="text-3xl font-bold text-blue-600">{summary.engagement_coverage?.stakeholder_types_engaged || 0}</p>
          <p className="text-sm text-blue-700">Stakeholder Types</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-200 text-center">
          <p className="text-3xl font-bold text-purple-600">{summary.engagement_coverage?.topics_with_input || 0}</p>
          <p className="text-sm text-purple-700">Topics Covered</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
          <p className="text-3xl font-bold text-amber-600">
            {Object.values(summary.by_topic || {}).length > 0 
              ? (Object.values(summary.by_topic).reduce((sum, t) => sum + (t.avg_rating || 0), 0) / 
                 Object.values(summary.by_topic).length).toFixed(1)
              : '0.0'}
          </p>
          <p className="text-sm text-amber-700">Avg Importance</p>
        </div>
      </div>

      {/* By Stakeholder Type */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h4 className="font-bold text-slate-900 mb-4">Engagement by Stakeholder Type</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(summary.by_stakeholder_type).map(([type, data]) => {
            const IconComponent = stakeholderIcons[type] || Users;
            return (
              <div key={type} className="p-3 bg-slate-50 rounded-lg text-center">
                <IconComponent size={24} className="mx-auto text-slate-600 mb-2" />
                <p className="font-medium text-slate-900 capitalize">{type.replace('_', ' ')}</p>
                <p className="text-sm text-slate-500">{data.count} engagements</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* By Topic */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h4 className="font-bold text-slate-900 mb-4">Topics by Stakeholder Priority</h4>
        <div className="space-y-3">
          {Object.entries(summary.by_topic)
            .sort((a, b) => (b[1].avg_rating || 0) - (a[1].avg_rating || 0))
            .slice(0, 5)
            .map(([topicId, data]) => (
              <div key={topicId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">{data.topic_name}</p>
                  <div className="flex items-center gap-1 mt-1">
                    {(data.stakeholder_types || []).slice(0, 3).map((st, idx) => {
                      const IconComponent = stakeholderIcons[st] || Users;
                      return <IconComponent key={idx} size={14} className="text-slate-400" />;
                    })}
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={14}
                        className={star <= Math.round(data.avg_rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{(data.avg_rating || 0).toFixed(1)} avg</p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

// Main Stakeholder Engagement Page
const StakeholderEngagement = () => {
  const [topics, setTopics] = useState({});
  const [engagements, setEngagements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('record');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [topicsData, summaryData, inputsData] = await Promise.all([
      apiFetch(`${API}/stakeholder-engagements/topics`),
      apiFetch(`${API}/stakeholder-engagements/summary`),
      apiFetch(`${API}/stakeholder-engagements`)
    ]);

    if (topicsData?._error || summaryData?._error || inputsData?._error) {
      console.error('Failed to fetch:', topicsData?.message || summaryData?.message || inputsData?.message);
      setLoading(false);
      return;
    }

    setTopics(topicsData.topics || {});
    setSummary(summaryData);
    // List endpoint returns { inputs: [...] }
    setEngagements(inputsData.inputs || []);
    setLoading(false);
  };

  const handleSubmitEngagement = async (formData) => {
    setSubmitting(true);
    const data = await apiFetch(`${API}/stakeholder-engagements`, { method: 'POST', body: formData });
    if (data?._error) {
      console.error('Failed to submit:', data.message);
      alert('Failed to record engagement. Please try again.');
      setSubmitting(false);
      return;
    }
    // Refresh data to show new engagement
    await fetchData();
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="animate-spin text-emerald-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stakeholder-engagement-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Stakeholder Engagement</h1>
            <p className="text-emerald-200">
              Track consultations, capture feedback, and document materiality input from stakeholders
            </p>
          </div>
          <div className="flex gap-4">
            <div className="text-center bg-white/10 rounded-lg px-4 py-2">
              <p className="text-2xl font-bold">{summary?.total_inputs || 0}</p>
              <p className="text-xs text-emerald-200">Engagements</p>
            </div>
            <div className="text-center bg-white/10 rounded-lg px-4 py-2">
              <p className="text-2xl font-bold">{summary?.engagement_coverage?.stakeholder_types_engaged || 0}</p>
              <p className="text-xs text-emerald-200">Stakeholder Types</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('record')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
            activeTab === 'record' 
              ? 'bg-emerald-100 text-emerald-700 border-b-2 border-emerald-600' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="tab-record"
        >
          <Plus className="inline mr-2" size={16} />
          Record Engagement
        </button>
        <button
          onClick={() => setActiveTab('summary')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
            activeTab === 'summary' 
              ? 'bg-emerald-100 text-emerald-700 border-b-2 border-emerald-600' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="tab-summary"
        >
          <BarChart3 className="inline mr-2" size={16} />
          Summary
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
            activeTab === 'history' 
              ? 'bg-emerald-100 text-emerald-700 border-b-2 border-emerald-600' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="tab-history"
        >
          <Clock className="inline mr-2" size={16} />
          History
        </button>
      </div>

      {/* Record Tab */}
      {activeTab === 'record' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-4">New Stakeholder Engagement</h3>
              <NewEngagementForm 
                topics={topics} 
                onSubmit={handleSubmitEngagement} 
                loading={submitting} 
              />
            </div>
          </div>

          {/* Quick Guide */}
          <div className="space-y-4">
            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5">
              <h4 className="font-bold text-emerald-900 mb-3">Why Track Stakeholder Input?</h4>
              <ul className="space-y-2 text-sm text-emerald-800">
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                  Required for ESRS double materiality assessment
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                  Demonstrates stakeholder consultation process
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                  Provides audit evidence for assurance
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                  Informs materiality threshold decisions
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h4 className="font-bold text-slate-900 mb-3">Engagement Methods</h4>
              <div className="space-y-2 text-sm text-slate-600">
                <p><strong>Surveys:</strong> Large-scale quantitative input</p>
                <p><strong>Interviews:</strong> Deep qualitative insights</p>
                <p><strong>Workshops:</strong> Collaborative prioritization</p>
                <p><strong>Advisory Panels:</strong> Ongoing expert guidance</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Tab */}
      {activeTab === 'summary' && (
        <StakeholderSummary summary={summary} />
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-900">Engagement History</h3>
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
            >
              <option value="all">All Types</option>
              {Object.keys(stakeholderIcons).map(type => (
                <option key={type} value={type}>{type.replace('_', ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>

          {engagements.length === 0 ? (
            <div className="text-center py-12 text-slate-500 bg-white rounded-xl border border-slate-200">
              <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
              <p>No engagements recorded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {engagements
                .filter(e => filterType === 'all' || e.stakeholder_type === filterType)
                .map((engagement, idx) => (
                  <EngagementCard key={idx} engagement={engagement} />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StakeholderEngagement;
